// assets/quiz-controller.js
// Cross-file deps: renderQuestionInto, decodeReplay.
// In browser (Shopify CDN): on window.* (relative imports break on CDN).
// In tests (Vitest): injected via setControllerDeps or set on globalThis.
const QUIZ_CONTROLLER_BUILD = 'audit-fixes-18';
if (typeof window !== 'undefined') {
  window.QUIZ_CONTROLLER_BUILD = QUIZ_CONTROLLER_BUILD;
  // eslint-disable-next-line no-console
  console.info('[quiz] controller build:', QUIZ_CONTROLLER_BUILD);
}
let _deps = {};
export function setControllerDeps(deps) { Object.assign(_deps, deps); }

export function createController({ root, questions, products, state, tracking, location = window.location }) {
  const renderQuestionInto = _deps.renderQuestionInto
    || (typeof window !== 'undefined' && window.renderQuestionInto);
  const decodeReplay = _deps.decodeReplay
    || (typeof window !== 'undefined' && window.decodeReplay);
  const mount = root.querySelector('.qz-mount');
  const questionCard = root.querySelector('.qz-question');

  const branchFor = () => {
    const goal = state.getAnswers().goal;
    return goal && questions.branches[goal] ? questions.branches[goal] : ['goal'];
  };

  // Questions that should be skipped based on current answers
  const shouldSkip = (questionId, answered) => {
    // Skip protein_type when diet is vegan or vegetarian (clear whey is dairy)
    if (questionId === 'protein_type') {
      const diet = answered.diet;
      if (Array.isArray(diet) && (diet.includes('vegan') || diet.includes('vegetarian'))) {
        return true;
      }
    }
    return false;
  };

  const currentStepIndex = () => {
    const answered = state.getAnswers();
    const branch = branchFor();
    for (let i = 0; i < branch.length; i++) {
      if (shouldSkip(branch[i], answered)) continue;
      if (answered[branch[i]] === undefined) return i;
    }
    return branch.length;
  };

  // Hydration (16e) — runs BEFORE tracking.emit('quiz_started')
  const params = new URLSearchParams(location.search || '');
  const replay = params.get('r');
  if (replay) {
    const result = decodeReplay(replay);
    if (result.ok) {
      state.reset();
      Object.entries(result.answers).forEach(([k, v]) => state.setAnswer(k, v));
      // Deep-link ?step=<question_id> (spec §7.1)
      const step = params.get('step');
      if (step) {
        const branch = branchFor();
        const targetIdx = branch.indexOf(step);
        if (targetIdx >= 0) {
          // Ensure all earlier steps are considered answered so currentStepIndex
          // lands on the requested step (deviation from plan: plan only cleared
          // forward, which failed the ?step=budget test when priorities was
          // unanswered — currentStepIndex would stop at priorities instead).
          for (let i = 0; i < targetIdx; i++) {
            const qid = branch[i];
            if (state.getAnswers()[qid] === undefined) state.setAnswer(qid, null);
          }
          // Clear the step itself and anything after it
          for (let i = targetIdx; i < branch.length; i++) state.clearAnswer(branch[i]);
        }
      }
    } else if (result.reason === 'unknown_version') {
      sessionStorage.setItem('qz-toast', 'version-changed');
      location.replace('/pages/quiz-page');
      return { render: () => {}, branchFor, currentStepIndex: () => 0 };
    } else {
      // malformed — log once per session
      if (!sessionStorage.getItem('qz-malformed-warned')) {
        console.warn('[quiz] Ignoring malformed ?r= replay param');
        sessionStorage.setItem('qz-malformed-warned', '1');
      }
    }
  }

  function render() {
    const branch = branchFor();
    const idx = currentStepIndex();
    if (idx >= branch.length) { renderComplete(); return; }

    const id = branch[idx];
    // Auto-skip questions that don't apply to current answers
    if (shouldSkip(id, state.getAnswers())) {
      state.setAnswer(id, null);
      render();
      return;
    }
    const q = { id, ...questions.questions[id] };
    const branchId = state.getAnswers().goal || null;

    questionCard.hidden = false;
    mount.appendChild(questionCard);

    // progress
    questionCard.querySelector('.qz-progress-fill').style.width = `${(idx / branch.length) * 100}%`;
    questionCard.querySelector('.qz-step-current').textContent = String(idx + 1);
    questionCard.querySelector('.qz-step-total').textContent = String(branch.length);

    renderQuestionInto(questionCard, q, { branchId });
    // Focus the prompt for screen readers WITHOUT forcing the page to jump
    // down on initial load. preventScroll is supported in all evergreen
    // browsers; focus is still set so aria-live/tab order work.
    try {
      questionCard.querySelector('.qz-prompt').focus({ preventScroll: true });
    } catch (_) {
      questionCard.querySelector('.qz-prompt').focus();
    }

    const next = questionCard.querySelector('.qz-next');
    const back = questionCard.querySelector('.qz-back');
    const skip = questionCard.querySelector('.qz-skip');
    next.disabled = true;
    // Visibility is controlled by the .qz-hidden class (styled with
    // `display: none !important` in the section stylesheet). This beats
    // any theme rule that might otherwise interfere with [hidden]. We
    // also mirror on the hidden attribute for accessibility semantics.
    const setHidden = (el, hide) => {
      el.classList.toggle('qz-hidden', !!hide);
      el.hidden = !!hide;
      el.style.display = '';
    };
    setHidden(back, idx === 0);
    setHidden(skip, !!q.required);

    const commit = () => {
      try {
        const vals = [...group.querySelectorAll('input:checked')].map(i => i.value);
        const value = q.type === 'single' ? vals[0] : vals;
        state.setAnswer(id, value);
        tracking.emit('quiz_question_answered', { question_id: id, answer_value: value });
        render();
      } catch (err) {
        console.error('[quiz] commit failed', err);
        next.disabled = false; // let the user retry via Next
      }
    };

    const group = questionCard.querySelector('.qz-options-wrap > [role]');

    // Robust input selection: bind `change` and `click` directly to every
    // input so we don't rely solely on bubbling `input` events (which some
    // older browsers and a few ad-blocker shims interfere with).
    const inputs = [...group.querySelectorAll('input')];
    let advanceScheduled = false;
    const onSelection = () => {
      const vals = inputs.filter(i => i.checked).map(i => i.value);
      next.disabled = vals.length === 0 || (q.type === 'multi' && q.max && vals.length > q.max);
      if (
        q.type === 'single'
        && vals.length === 1
        && !next.disabled
        && !advanceScheduled
      ) {
        advanceScheduled = true;
        // Don't disable Next here — that blocks a user who clicks Next fast
        // (and breaks synchronous test clicks). The scheduled commit guards
        // itself by checking whether the group is still in the DOM.
        setTimeout(() => {
          if (document.body.contains(group) && group.querySelector('input:checked')) {
            commit();
          }
        }, 150);
      }
    };
    inputs.forEach(inp => {
      inp.addEventListener('change', onSelection);
      inp.addEventListener('click', onSelection);
    });
    // Keep the bubbled `input` handler too, as a belt-and-braces backup.
    group.addEventListener('input', onSelection);

    next.onclick = () => { commit(); };

    back.onclick = () => {
      const branch = branchFor();
      for (let i = idx - 1; i >= 0; i--) {
        const prevId = branch[i];
        if (shouldSkip(prevId, state.getAnswers())) continue;
        for (let j = i; j < branch.length; j++) state.clearAnswer(branch[j]);
        render();
        return;
      }
    };

    skip.onclick = () => {
      state.setAnswer(id, null);
      render();
    };

    tracking.emit('quiz_question_viewed', { question_id: id });
  }

  function renderComplete() {
    questionCard.hidden = true;
    root.dispatchEvent(new CustomEvent('quiz:complete', {
      detail: { answers: state.getAnswers(), state, products },
    }));
  }

  tracking.emit('quiz_started');
  render();

  return { render, branchFor, currentStepIndex };
}

// Bootstrap (Step A) — production entry point
if (typeof window !== 'undefined') {
  window.createQuizController = createController;
  window.addEventListener('DOMContentLoaded', async () => {
    const root = document.getElementById('quiz-root');
    if (!root) return;
    const cfg = window.QUIZ_CONFIG;
    if (!cfg) return;
    const [questions, products] = await Promise.all([
      fetch(cfg.questionsUrl).then(r => r.json()),
      fetch(cfg.productsUrl).then(r => r.json()),
    ]);
    const state = new window.QuizState();
    const replayUrlGetter = () => {
      const payload = { v: 1, sid: state.sessionId, answers: state.getAnswers() };
      return `${location.origin}/pages/quiz-page?r=${window.encodeReplay(payload)}`;
    };
    const tracking = new window.QuizTracking({
      sessionId: state.sessionId,
      version: cfg.version,
      getReplayUrl: replayUrlGetter,
    });
    if (window.QUIZ_CUSTOMER_EMAIL) tracking.setEmail(window.QUIZ_CUSTOMER_EMAIL);
    createController({ root, questions, products, state, tracking });
    tracking.startAbandonWatch?.(() => state);
  });
}
